import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { checkListService, imprimirCheckList } from '../../services/checkListService';
import apiClient from '../../services/apiClient';
import SearchableSelectField from '../../components/common/SearchableSelectField';
import Modal from '../../components/common/Modal';
import ConfiguracionBase from '../../components/configuracion/ConfiguracionBase';
import { useConfirm } from '../../components/common/ModalConfirmacion';
import ImportarModal from '../../components/importacion/ImportarModal';
import { TIMEOUT_IMPORTACION_MS } from '../../services/importacionService';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';

const HELP = {
  prereqs: [
    'Tener cargados los puntos de control que conformarán el check list.',
    'Tener cargadas las familias de los equipos para poder asignar la plantilla a cada familia.',
  ],
  tips: [
    'Cada familia de equipo tiene su propia plantilla: los controles de una excavadora no son los mismos que los de un camión.',
    'Para armar una plantilla parecida a otra ya existente, usá el botón de clonar y ajustá las diferencias.',
    'Los puntos se agrupan por sistema, así el operador recorre el equipo de forma ordenada.',
    'La opción de imprimir en blanco sirve para revisar el equipo en papel cuando no hay acceso al check list digital.',
  ],
  nextSteps: [
    'Los usuarios habilitados completan el check list desde el portal del equipo.',
    'Consultar el informe de check lists para ver los controles realizados y las fallas detectadas.',
    'Podes generar una orden de trabajo con los puntos de control que hayan fallado en un check list, para que el taller realice las tareas de reparación.',
  ],
};

// ── Styled components ────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 1rem 1.25rem 2rem;
  background: ${({ theme }) => theme.colors.bgPrincipal};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  height: calc((100vh / var(--app-zoom, 1)) - 80px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-shrink: 0;
  h1 {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
  }
  p {
    margin: 0;
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.txtSecondary};
  }
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 13px;
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  @media (max-width: 768px) {
    min-height: 320px;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-weight: 600;
  font-size: 0.85rem;
  background: ${({ theme }) => theme.colors.surface};
  flex-shrink: 0;
`;

// Antes tenía max-height: 60vh fijo: en plantillas con pocos puntos sobraba
// media pantalla en negro sin usar, y en plantillas con muchos puntos se
// cortaba mucho antes del pie de página aunque hubiera lugar de sobra. Ahora
// ocupa exactamente el espacio disponible del panel y scrollea adentro.
const PanelBody = styled.div`
  padding: 0.75rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const NuevoPuntoSection = styled.div`
  padding: 0.5rem 0.75rem 0.6rem;
  border-bottom: 2px dashed ${({ theme }) => theme.colors.primary + '50'};
  background: ${({ theme }) => theme.colors.primary + '07'};
  flex-shrink: 0;
`;

const NuevoPuntoLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.3rem;
  padding-left: 0.5rem;
`;

const NuevoPuntoLabelText = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.7;
`;

const BtnNuevoCatalogo = styled.button`
  border: none;
  border-radius: 5px;
  padding: 0.2rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: ${({ theme }) => theme.colors.info};
  color: ${({ theme }) => theme.colors.onPrimary};
  transition: background 0.15s;
  &:hover { background: ${({ theme }) => theme.colors.primaryHover}; }
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ $danger, theme }) => ($danger ? theme.colors.danger : theme.colors.txtSecondary)};
  padding: 0.25rem 0.35rem;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;
  flex-shrink: 0;
  &:hover {
    color: ${({ $danger, theme }) => ($danger ? theme.colors.danger : theme.colors.txtPrincipal)};
    background: ${({ theme }) => theme.colors.surfaceHover};
    opacity: 1 !important;
  }
`;

const PlantillaItem = styled.div`
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.82rem;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary + '22' : 'transparent')};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.txtPrincipal)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  transition-property: background-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;
  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.primary + '33' : theme.colors.surfaceHover};
  }
  &:hover ${IconBtn} { opacity: 1; }
  &:not(:last-child) { margin-bottom: 0.25rem; }
`;

// El nombre de la plantilla puede envolver a dos líneas (familias con nombre
// largo, p.ej. "Check list Bomba de Hormigón); sin min-width:0 un flex item no
// puede achicarse por debajo de su contenido, así que en vez de envolver el
// título empujaba y apretaba el badge de al lado hasta partir "138 pts" en dos.
const PlantillaTitulo = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

const Badge = styled.span`
  font-size: 0.72rem;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 10px;
  padding: 1px 7px;
  color: ${({ theme }) => theme.colors.txtSecondary};
  white-space: nowrap;
`;

const Btn = styled.button`
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: all 0.15s;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PrimaryBtn = styled(Btn)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  &:hover { background: ${({ theme }) => theme.colors.primaryHover}; }
`;

const SecondaryBtn = styled(Btn)`
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; opacity: 1; }
`;


const FormPanel = styled.div`
  padding: 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  flex-shrink: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 0.75rem;
  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
    color: ${({ theme }) => theme.colors.txtSecondary};
  }
  input, select {
    width: 100%;
    padding: 0.4rem 0.5rem;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.bgPrincipal};
    color: ${({ theme }) => theme.colors.txtPrincipal};
    font-size: 0.82rem;
    box-sizing: border-box;
    &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  }
`;

// Grid: catalogo | [sistema al agregar] | tipo | delete
const PuntoRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $nuevo }) => $nuevo ? '1fr 160px 120px 36px' : '1fr 130px 36px'};
  gap: 0.4rem;
  align-items: center;
  padding: 0.4rem 0.5rem;
  border-radius: 6px;
  border: 1px solid ${({ $nuevo, theme }) => ($nuevo ? theme.colors.primary + '66' : theme.colors.border)};
  margin-bottom: 0.3rem;
  background: ${({ $nuevo, theme }) => ($nuevo ? theme.colors.primary + '08' : theme.colors.bgPrincipal)};
  font-size: 0.8rem;
  position: relative;

  &:hover ${IconBtn} { opacity: 1; }

  select {
    padding: 0.3rem 0.4rem;
    border-radius: 5px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.bgSecundario};
    color: ${({ theme }) => theme.colors.txtPrincipal};
    font-size: 0.8rem;
    width: 100%;
    box-sizing: border-box;
    &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  }
`;

const ColHeaders = styled.div`
  display: grid;
  grid-template-columns: ${({ $nuevo }) => $nuevo ? '1fr 160px 120px 36px' : '1fr 130px 36px'};
  gap: 0.4rem;
  padding: 0 0.5rem;
  margin-bottom: 0.25rem;
  span {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.txtSecondary};
    opacity: 0.6;
  }
`;

const SistemaGroup = styled.div`
  margin-bottom: 1rem;
`;

const SistemaLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.txtSecondary};
  padding: 0.3rem 0.5rem;
  margin-bottom: 0.35rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;


const TipoBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: ${({ $tipo, theme }) => $tipo === 'presencia' ? theme.colors.info + '22' : theme.colors.primary + '22'};
  color: ${({ $tipo, theme }) => $tipo === 'presencia' ? theme.colors.info : theme.colors.primary};
  white-space: nowrap;
`;

const SistemaHint = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;


const EmptyMsg = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: 0.82rem;
  i { font-size: 2rem; display: block; margin-bottom: 0.5rem; opacity: 0.4; }
`;

const PuntosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.4rem;
  padding: 0 0.25rem;
`;

const PuntoCard = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 5px;
  padding: 0.18rem 0.3rem 0.18rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  height: 28px;
  &:hover ${IconBtn} { opacity: 1; }
`;

const PuntoCardNombre = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.76rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PuntoCardFooter = styled.div`
  display: contents;
`;

const Inline = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

// El badge de recuento + los íconos de acción no deben achicarse: si lo hacen,
// "138 pts" se parte en dos líneas dentro del badge. Ver PlantillaTitulo.
const PlantillaAcciones = styled(Inline)`
  flex-shrink: 0;
`;

const normalize = (d) => (Array.isArray(d) ? d : d?.results || []);

// ── Fila de nuevo punto ───────────────────────────────────────────────────────

const NuevoPuntoRow = ({ plantillaId, catalogoPuntos, onCreado, preselect, onPreselectConsumed }) => {
  const [nombreSeleccionado, setNombreSeleccionado] = useState(null);
  const [sistemaSeleccionado, setSistemaSeleccionado] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Opciones de nombres únicos para el primer selector
  const opcionesNombre = useMemo(() => {
    const vistos = new Set();
    return catalogoPuntos
      .filter((c) => { if (vistos.has(c.nombre)) return false; vistos.add(c.nombre); return true; })
      .map((c) => ({ value: c.nombre, label: c.nombre }));
  }, [catalogoPuntos]);

  // Entradas del catálogo que coinciden con el nombre elegido
  const entradasDelNombre = useMemo(() => {
    if (!nombreSeleccionado) return [];
    return catalogoPuntos.filter((c) => c.nombre === nombreSeleccionado);
  }, [catalogoPuntos, nombreSeleccionado]);

  const tieneMultiplesSistemas = entradasDelNombre.length > 1;

  // catObj resuelto: si hay uno solo se toma directo; si hay varios, por el sistema elegido
  const catObj = useMemo(() => {
    if (!entradasDelNombre.length) return null;
    if (!tieneMultiplesSistemas) return entradasDelNombre[0];
    return entradasDelNombre.find((c) => String(c.sistema) === String(sistemaSeleccionado)) || null;
  }, [entradasDelNombre, tieneMultiplesSistemas, sistemaSeleccionado]);

  const tipoEstado = catObj?.tipo_estado_default || 'condicion';

  // Al cambiar nombre, resetear sistema y auto-seleccionar si hay uno solo
  const handleNombreChange = (nombre) => {
    setNombreSeleccionado(nombre || null);
    setSistemaSeleccionado(null);
  };

  // Auto-seleccionar cuando se crea un nuevo punto desde el modal
  useEffect(() => {
    if (preselect == null) return;
    const entrada = catalogoPuntos.find((c) => String(c.id) === String(preselect));
    if (entrada) {
      setNombreSeleccionado(entrada.nombre);
      setSistemaSeleccionado(entrada.sistema ? String(entrada.sistema) : null);
    }
    onPreselectConsumed?.();
  }, [preselect]); // eslint-disable-line react-hooks/exhaustive-deps

  const puedeGuardar = catObj && (!tieneMultiplesSistemas || sistemaSeleccionado);

  const handleGuardar = async () => {
    if (!catObj) return;
    setGuardando(true);
    try {
      const creado = await checkListService.crearPunto({
        plantilla: plantillaId,
        catalogo: catObj.id,
        nombre: catObj.nombre,
        sistema: catObj.sistema || null,
        tipo_estado: tipoEstado,
        orden: 999,
      });
      setNombreSeleccionado(null);
      setSistemaSeleccionado(null);
      onCreado(creado);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <PuntoRow $nuevo>
      <SearchableSelectField
        value={nombreSeleccionado}
        onChange={handleNombreChange}
        options={opcionesNombre}
        placeholder="Buscar punto de control..."
      />
      {tieneMultiplesSistemas ? (
        <select
          value={sistemaSeleccionado || ''}
          onChange={(e) => setSistemaSeleccionado(e.target.value || null)}
        >
          <option value="">Elegir sistema…</option>
          {entradasDelNombre.map((c) => (
            <option key={c.id} value={String(c.sistema)}>{c.sistema_nombre || `ID ${c.sistema}`}</option>
          ))}
        </select>
      ) : (
        catObj?.sistema_nombre
          ? <SistemaHint>{catObj.sistema_nombre}</SistemaHint>
          : <span />
      )}
      {catObj
        ? <TipoBadge $tipo={tipoEstado}>{tipoEstado === 'presencia' ? 'P / NP' : 'B / R / M'}</TipoBadge>
        : <span />}
      <PrimaryBtn
        onClick={handleGuardar}
        disabled={guardando || !puedeGuardar}
        style={{ padding: '0.3rem 0.5rem', minWidth: 0 }}
        title="Agregar punto"
      >
        {guardando ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
      </PrimaryBtn>
    </PuntoRow>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────

const ConfiguracionCheckList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backTo = (() => { const f = searchParams.get('from'); return f ? `/configuracion?seccion=${f}` : '/configuracion?seccion=mant'; })();
  const theme = useTheme();
  const { confirmar } = useConfirm();
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaActiva, setPlantillaActiva] = useState(null);
  const [puntos, setPuntos] = useState([]);
  const [puntosPlantillaId, setPuntosPlantillaId] = useState(null);
  const [catalogoPuntos, setCatalogoPuntos] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [showNuevoPuntoModal, setShowNuevoPuntoModal] = useState(false);
  const [preselectCatalogoId, setPreselectCatalogoId] = useState(null);
  const [showImportarModal, setShowImportarModal] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Form plantilla (create / edit / clone)
  const [formMode, setFormMode] = useState(null); // null | 'create' | 'edit' | 'clone'
  const [formPlantilla, setFormPlantilla] = useState(null);
  const [formFamilia, setFormFamilia] = useState(null);
  const [formNombre, setFormNombre] = useState('');

  const cargarPlantillas = useCallback(async () => {
    setLoading(true);
    try {
      const data = await checkListService.listarPlantillas();
      setPlantillas(normalize(data));
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarPuntos = useCallback(async (plantillaId) => {
    const data = await checkListService.listarPuntos({ plantilla: plantillaId });
    setPuntos(normalize(data));
    setPuntosPlantillaId(plantillaId);
  }, []);

  const recargarCatalogo = useCallback(() => {
    apiClient.get('/catalogo-puntos-control/?page_size=5000')
      .then((r) => setCatalogoPuntos(normalize(r.data)));
  }, []);

  useEffect(() => {
    cargarPlantillas();
    apiClient.get('/catalogo-puntos-control/?page_size=5000').then((r) => setCatalogoPuntos(normalize(r.data)));
    apiClient.get('/familias/?page_size=500').then((r) => setFamilias(normalize(r.data)));
  }, [cargarPlantillas]);

  useEffect(() => {
    if (!plantillaActiva) { setPuntos([]); setPuntosPlantillaId(null); return; }
    // Si ya tenemos los puntos de esta plantilla en memoria (p.ej. recién
    // clonada, con todo incluido en la respuesta) no hace falta re-pedirlos.
    if (puntosPlantillaId === plantillaActiva.id) return;
    cargarPuntos(plantillaActiva.id);
  }, [plantillaActiva, cargarPuntos, puntosPlantillaId]);

  const cerrarForm = () => { setFormMode(null); setFormPlantilla(null); setFormFamilia(null); setFormNombre(''); };

  const abrirEditar = (p, e) => {
    e.stopPropagation();
    setFormMode('edit');
    setFormPlantilla(p);
    setFormFamilia(p.familia);
    setFormNombre(p.nombre);
  };

  const abrirClonar = (p, e) => {
    e.stopPropagation();
    setFormMode('clone');
    setFormPlantilla(p);
    setFormFamilia(null);
    setFormNombre(`Copia de ${p.nombre}`);
  };

  // La API ya devuelve la plantilla completa en la respuesta del POST/PATCH:
  // no hace falta releer la lista entera para reflejar un alta o un rename.
  const aResumen = (p) => ({
    id: p.id, familia: p.familia, familia_nombre: p.familia_nombre,
    nombre: p.nombre, activa: p.activa, total_puntos: p.total_puntos,
  });

  const handleGuardarPlantilla = async () => {
    if (!formFamilia || !formNombre.trim()) return;
    setGuardando(true);
    try {
      if (formMode === 'create') {
        const creada = await checkListService.crearPlantilla({ familia: formFamilia, nombre: formNombre.trim() });
        setPlantillas((prev) => [...prev, aResumen(creada)]);
        setPlantillaActiva(creada);
      } else if (formMode === 'edit') {
        const actualizada = await checkListService.actualizarPlantilla(formPlantilla.id, { familia: formFamilia, nombre: formNombre.trim() });
        const resumen = aResumen(actualizada);
        setPlantillas((prev) => prev.map((p) => (p.id === formPlantilla.id ? resumen : p)));
        if (plantillaActiva?.id === formPlantilla.id) setPlantillaActiva(resumen);
      }
      cerrarForm();
    } finally {
      setGuardando(false);
    }
  };

  const handleClonarPlantilla = async () => {
    if (!formFamilia || !formNombre.trim() || !formPlantilla) return;
    setGuardando(true);
    try {
      // Clonado en un solo request: el backend crea la plantilla y copia
      // todos sus puntos con un bulk_create. Antes era un POST por punto,
      // en serie — con plantillas grandes eso eran minutos de espera.
      const nueva = await checkListService.clonarPlantilla(formPlantilla.id, {
        familia: formFamilia,
        nombre: formNombre.trim(),
      });
      setPlantillas((prev) => [...prev, aResumen(nueva)]);
      setPuntos(normalize(nueva.puntos_control));
      setPuntosPlantillaId(nueva.id);
      setPlantillaActiva(nueva);
      cerrarForm();
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminarPlantilla = async (p) => {
    const ok = await confirmar({ titulo: `¿Eliminar plantilla "${p.nombre}"?`, mensaje: 'Se eliminarán todos sus puntos de control. Esta acción no se puede deshacer.', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return;
    try {
      await checkListService.eliminarPlantilla(p.id);
    } catch (err) {
      // Sin este catch, un borrado bloqueado en el servidor (p.ej. la plantilla
      // tiene checklists ya realizados) fallaba en silencio: no se veía ningún
      // error y la plantilla seguía ahí, bloqueando cualquier alta nueva para
      // esa familia hasta que se refrescaba la página a mano.
      await confirmar({
        titulo: 'No se pudo eliminar',
        mensaje: err.response?.data?.detail || 'Ocurrió un error al eliminar la plantilla.',
        labelConfirmar: 'Entendido',
        labelCancelar: 'Cerrar',
        variante: 'warning',
      });
      return;
    }
    if (plantillaActiva?.id === p.id) setPlantillaActiva(null);
    setPlantillas((prev) => prev.filter((pl) => pl.id !== p.id));
  };

  // El badge de "N pts" del panel izquierdo se mantiene sincronizado con la
  // cantidad real de puntos cargados en memoria para la plantilla activa, sin
  // depender de un refresco de `plantillas` — cubre alta, baja e importación.
  useEffect(() => {
    if (!plantillaActiva || puntosPlantillaId !== plantillaActiva.id) return;
    setPlantillas((prev) => {
      const actual = prev.find((p) => p.id === plantillaActiva.id);
      if (!actual || actual.total_puntos === puntos.length) return prev;
      return prev.map((p) => (p.id === plantillaActiva.id ? { ...p, total_puntos: puntos.length } : p));
    });
  }, [puntos, puntosPlantillaId, plantillaActiva]);

  const handleEliminarPunto = async (puntoId) => {
    const puntoEliminado = puntos.find((p) => p.id === puntoId);
    // Optimista: se retira de la vista antes de que vuelva la respuesta del
    // servidor; si falla, se restaura.
    setPuntos((prev) => prev.filter((p) => p.id !== puntoId));
    try {
      await checkListService.eliminarPunto(puntoId);
    } catch (err) {
      if (puntoEliminado) setPuntos((prev) => [...prev, puntoEliminado]);
      throw err;
    }
  };

  // Agrupar puntos por sistema
  const puntosPorSistema = puntos.reduce((acc, p) => {
    const key = p.sistema_nombre || 'Sin sistema';
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  const familiasFiltradas = familias.filter((f) => {
    if (formMode === 'edit') return !plantillas.some((p) => p.familia === f.id && p.id !== formPlantilla?.id);
    return !plantillas.some((p) => p.familia === f.id);
  });

  return (
    <Page>
      <PageHeader>
        <div>
          <h1><i className="fas fa-clipboard-check" style={{ marginRight: '0.5rem' }} />Check List de Equipos</h1>
          <p>Configurá las plantillas de puntos de control por familia de equipo</p>
        </div>
        <Inline>
          <HelpToggleBtn
            type="button"
            $active={helpOpen}
            onClick={() => setHelpOpen((v) => !v)}
          >
            <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
            Ayuda
          </HelpToggleBtn>
          <BackBtn onClick={() => navigate(backTo)}>
            <i className="fas fa-arrow-left" /> Volver
          </BackBtn>
        </Inline>
      </PageHeader>

      {helpOpen && (
        <div style={{ marginBottom: '1rem', flexShrink: 0 }}>
          <HelpPanel
            prereqs={HELP.prereqs}
            tips={HELP.tips}
            nextSteps={HELP.nextSteps}
            inlineOpen
          />
        </div>
      )}

      <Layout>
        {/* Panel izquierdo – lista de plantillas */}
        <Panel>
          <PanelHeader>
            <span>Plantillas</span>
            <PrimaryBtn onClick={() => formMode ? cerrarForm() : setFormMode('create')}>
              {formMode ? <i className="fas fa-times" /> : <i className="fas fa-plus" />}
              {formMode ? 'Cancelar' : 'Nueva'}
            </PrimaryBtn>
          </PanelHeader>

          {formMode && (
            <FormPanel>
              {formMode === 'clone' && (
                <div style={{ fontSize: '0.75rem', marginBottom: '0.6rem', opacity: 0.65 }}>
                  <i className="fas fa-clone" style={{ marginRight: 5 }} />
                  Clonando: <strong>{formPlantilla?.nombre}</strong>
                </div>
              )}
              <FormGroup>
                <label>Familia de equipo</label>
                <SearchableSelectField
                  value={formFamilia}
                  onChange={(v) => {
                    setFormFamilia(v);
                    if (formMode === 'create') {
                      const fam = familias.find((f) => f.id === v);
                      if (fam && !formNombre) setFormNombre(`Check List – ${fam.nombre}`);
                    }
                  }}
                  options={familiasFiltradas.map((f) => ({ value: f.id, label: f.nombre }))}
                  placeholder="Seleccioná familia..."
                />
              </FormGroup>
              <FormGroup>
                <label>Nombre de la plantilla</label>
                <input
                  value={formNombre}
                  onChange={(e) => setFormNombre(e.target.value)}
                  placeholder="Nombre descriptivo"
                />
              </FormGroup>
              <Inline>
                <PrimaryBtn
                  onClick={formMode === 'clone' ? handleClonarPlantilla : handleGuardarPlantilla}
                  disabled={guardando || !formFamilia}
                >
                  {guardando ? <i className="fas fa-spinner fa-spin" /> : null}
                  {formMode === 'edit' ? 'Guardar cambios' : formMode === 'clone' ? 'Clonar' : 'Guardar'}
                </PrimaryBtn>
                <SecondaryBtn onClick={cerrarForm}>Cancelar</SecondaryBtn>
              </Inline>
            </FormPanel>
          )}

          <PanelBody>
            {loading && <EmptyMsg><i className="fas fa-spinner fa-spin" />Cargando...</EmptyMsg>}
            {!loading && plantillas.length === 0 && (
              <EmptyMsg><i className="fas fa-clipboard" />No hay plantillas. Creá la primera.</EmptyMsg>
            )}
            {plantillas.map((p) => (
              <PlantillaItem
                key={p.id}
                $active={plantillaActiva?.id === p.id}
                onClick={() => setPlantillaActiva(p)}
              >
                <PlantillaTitulo>
                  <div>{p.nombre}</div>
                  <div style={{ fontSize: '0.72rem', opacity: 0.65, marginTop: 2 }}>{p.familia_nombre}</div>
                </PlantillaTitulo>
                <PlantillaAcciones>
                  <Badge>{p.total_puntos} pts</Badge>
                  <IconBtn onClick={(e) => abrirEditar(p, e)} title="Editar plantilla">
                    <i className="fas fa-pencil" />
                  </IconBtn>
                  <IconBtn onClick={(e) => abrirClonar(p, e)} title="Clonar plantilla">
                    <i className="fas fa-clone" />
                  </IconBtn>
                  <IconBtn $danger onClick={(e) => { e.stopPropagation(); handleEliminarPlantilla(p); }} title="Eliminar plantilla">
                    <i className="fas fa-trash" />
                  </IconBtn>
                </PlantillaAcciones>
              </PlantillaItem>
            ))}
          </PanelBody>
        </Panel>

        {/* Panel derecho – puntos de control */}
        <Panel>
          <PanelHeader>
            <span>
              {plantillaActiva
                ? `Puntos de control – ${plantillaActiva.nombre}`
                : 'Seleccioná una plantilla'}
            </span>
            {plantillaActiva && (
              <Inline>
                <SecondaryBtn
                  onClick={() => setShowImportarModal(true)}
                  title="Importar puntos de control desde Excel"
                  style={{ padding: '0.25rem 0.55rem', fontSize: '0.78rem' }}
                >
                  <i className="fas fa-file-import" style={{ marginRight: 4 }} />
                  Importar
                </SecondaryBtn>
                <SecondaryBtn
                  onClick={() => imprimirCheckList({ plantillaId: plantillaActiva.id, theme })}
                  title="Imprimir plantilla en blanco"
                  style={{ padding: '0.25rem 0.55rem', fontSize: '0.78rem' }}
                >
                  <i className="fas fa-print" style={{ marginRight: 4 }} />
                  Imprimir en blanco
                </SecondaryBtn>
              </Inline>
            )}
          </PanelHeader>

          {/* Fila de nuevo punto FIJA AL TOPE (fuera del scroll) */}
          {plantillaActiva && (
            <NuevoPuntoSection>
              <NuevoPuntoLabelRow>
                <NuevoPuntoLabelText>
                  <i className="fas fa-plus" style={{ marginRight: 5 }} />Nuevo punto
                </NuevoPuntoLabelText>
                <BtnNuevoCatalogo
                  onClick={() => setShowNuevoPuntoModal(true)}
                  title="Agregar nuevo punto al catálogo"
                >
                  <i className="fas fa-list-check" /> Nuevo punto de control
                </BtnNuevoCatalogo>
              </NuevoPuntoLabelRow>
              <ColHeaders $nuevo>
                <span>Punto de control</span>
                <span>Sistema</span>
                <span>Tipo</span>
                <span />
              </ColHeaders>
              <NuevoPuntoRow
                plantillaId={plantillaActiva.id}

                catalogoPuntos={catalogoPuntos.filter((c) => !puntos.some((p) => String(p.catalogo) === String(c.id)))}
                onCreado={(creado) => setPuntos((prev) => [...prev, creado])}
                preselect={preselectCatalogoId}
                onPreselectConsumed={() => setPreselectCatalogoId(null)}
              />
            </NuevoPuntoSection>
          )}

          <PanelBody>
            {!plantillaActiva && (
              <EmptyMsg>
                <i className="fas fa-arrow-left" />
                Elegí una plantilla a la izquierda para ver y editar sus puntos de control.
              </EmptyMsg>
            )}

            {plantillaActiva && (
              <>
                {puntos.length === 0 && (
                  <div style={{ fontSize: '0.8rem', opacity: 0.5, padding: '1rem 0.5rem', textAlign: 'center' }}>
                    No hay puntos aún. Agregá el primero arriba.
                  </div>
                )}

                {Object.entries(puntosPorSistema).map(([sistNombre, pts]) => (
                  <SistemaGroup key={sistNombre}>
                    <SistemaLabel>
                      <i className="fas fa-layer-group" style={{ marginRight: 6 }} />
                      {sistNombre}
                    </SistemaLabel>
                    <PuntosGrid>
                      {pts.map((punto) => (
                        <PuntoCard key={punto.id}>
                          <PuntoCardNombre title={punto.nombre}>{punto.nombre}</PuntoCardNombre>
                          <PuntoCardFooter>
                            <TipoBadge $tipo={punto.tipo_estado}>
                              {punto.tipo_estado === 'presencia' ? 'P / NP' : 'B / R / M'}
                            </TipoBadge>
                            <IconBtn $danger onClick={() => handleEliminarPunto(punto.id)} title="Eliminar punto">
                              <i className="fas fa-trash" />
                            </IconBtn>
                          </PuntoCardFooter>
                        </PuntoCard>
                      ))}
                    </PuntosGrid>
                  </SistemaGroup>
                ))}
              </>
            )}
          </PanelBody>
        </Panel>
      </Layout>

      {/* Modal – importar puntos de control desde Excel */}
      {showImportarModal && plantillaActiva && (
        <ImportarModal
          entidad="puntos-control-plantilla"
          onClose={() => {
            setShowImportarModal(false);
            cargarPuntos(plantillaActiva.id);
          }}
          onImportado={() => cargarPuntos(plantillaActiva.id)}
          onDescargarPlantilla={async () => {
            const resp = await apiClient.get('importar-puntos-plantilla/plantilla/', { responseType: 'blob' });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'plantilla_puntos_checklist.xlsx';
            a.click();
            URL.revokeObjectURL(url);
          }}
          onImportar={async (archivo) => {
            const fd = new FormData();
            fd.append('archivo', archivo);
            const resp = await apiClient.post(
              `importar-puntos-plantilla/?plantilla_id=${plantillaActiva.id}`,
              fd,
              {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: TIMEOUT_IMPORTACION_MS,
              },
            );
            return resp.data;
          }}
        />
      )}

      {/* Modal – agregar nuevo punto al catálogo sin salir de la página */}
      {showNuevoPuntoModal && (
        <Modal
          isOpen={showNuevoPuntoModal}
          onClose={() => {
            setShowNuevoPuntoModal(false);
            recargarCatalogo();
          }}
          title="Nuevo punto de control"
          size="md"
          minHeight="640px"
          maxHeight="92vh"
          backdropBg={theme.colors.overlayBackdropLight}
        >
          <ConfiguracionBase
            apiEndpoint="catalogo-puntos-control"
            titulo="Puntos de Control"
            icon="fas fa-list-check"
            modalOnly
            autoOpenAdd
            compact
            onRecordSaved={(saved) => {
              // Agregar optimísticamente al catálogo para que NuevoPuntoRow lo encuentre al pre-seleccionar
              setCatalogoPuntos((prev) => {
                const existe = prev.some((c) => String(c.id) === String(saved.id));
                return existe ? prev : [...prev, saved];
              });
              setPreselectCatalogoId(String(saved.id));
            }}
            onRequestClose={() => {
              setShowNuevoPuntoModal(false);
              recargarCatalogo();
            }}
          />
        </Modal>
      )}
    </Page>
  );
};

export default ConfiguracionCheckList;
